/** @file The absolute bone value whose setup-relative form is exactly a stored key value (R17-03). */

const bits = new BigInt64Array(1);
const floats = new Float64Array(bits.buffer);

/** `value` moved by `ulps` units in the last place (through its bit pattern; may cross zero into NaN, checked). */
function nudge(value: number, ulps: number): number {
  floats[0] = value;
  bits[0] = (bits[0] ?? 0n) + BigInt(ulps);
  return floats[0];
}

/**
 * animation.setKey stores `absolute - setup` (rotation, x, y, shear) or `absolute / setup` (scaleX, scaleY). This is
 * the inverse that survives the round trip: a float whose relative form is `relative` again (the plain sum or product,
 * else within a few ulps of it), written with the fewest significant digits that still do, so sending a read key back
 * stores the same number. A value with no exact preimage comes back as the plain sum or product.
 */
export function absoluteValue(relative: number, setup: number, scale: boolean): number {
  const back = (absolute: number): number => (scale ? absolute / setup : absolute - setup);
  const first = scale ? relative * setup : relative + setup;
  const exact = back(first) === relative ? first : nearby(first, (candidate) => back(candidate) === relative);
  return exact === undefined ? first : shortest(exact, (candidate) => back(candidate) === relative);
}

/** A float within 4 ulps of `value` that passes `ok`, if any. */
function nearby(value: number, ok: (candidate: number) => boolean): number | undefined {
  for (let ulps = 1; ulps <= 4; ulps++) {
    for (const candidate of [nudge(value, ulps), nudge(value, -ulps)]) if (Number.isFinite(candidate) && ok(candidate)) return candidate;
  }
  return undefined;
}

/** The shortest decimal (fewest significant digits) that still passes `ok`: 0.1, not 0.09999999999999964 (M9). */
function shortest(value: number, ok: (candidate: number) => boolean): number {
  for (let digits = 1; digits < 17; digits++) {
    const candidate = Number(value.toPrecision(digits));
    if (ok(candidate)) return candidate;
  }
  return value;
}
